import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
    FormBox,
    FormBoxSelection,
    FormBoxTitle,
    Main_Plain
} from "monica-alexandria";
import { config } from "../../../../config";
import { Sales_Lead_Avatar } from "./Sales_Lead_Avatar";
import { Sales_Lead_Quill_Field } from "./Sales_Lead_Quill_Field";

const ProspectLeadComments = styled.div`
    display: grid;
    gap: 1.4rem;
`;

const ProspectLeadCommentList = styled.div`
    display: grid;
    gap: 1rem;
`;

const ProspectLeadComment = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;
    align-items: start;
`;

const ProspectLeadCommentMeta = styled.div`
    display: grid;
    gap: 0.3rem;

    h5,
    h6 {
        color: ${p => p.theme.color};
    }

    h6 {
        opacity: 0.8;
    }
`;

const ProspectLeadCommentBody = styled.div`
    display: grid;
    gap: 1rem;

    p {
        margin: 0;
        color: ${p => p.theme.color};
    }

    div {
        display: grid;
        gap: 0.8rem;
        font-size: 1.6rem;
        font-family: "fontMedium", sans-serif;
    }

    ul,
    ol {
        margin: 0;
        padding-left: 2rem;
    }

    li {
        color: ${p => p.theme.color};
    }
`;

/**
 * Escapes plain comment text before wrapping it in typography tags.
 */
const read_safe_html_text = (value) => {
    return `${value || ""}`
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
};

/**
 * Reads saved lead actions as comments for the comment section.
 */
const read_comment_actions = (lead) => {
    return (lead?.actions || []).filter((action) => action?.action === "Note" && action?.comment).sort((firstAction, secondAction) => {
        const firstDate = new Date(firstAction?.timestamp || firstAction?.createdAt || firstAction?.created || firstAction?.updatedAt || 0);
        const secondDate = new Date(secondAction?.timestamp || secondAction?.createdAt || secondAction?.created || secondAction?.updatedAt || 0);

        return secondDate.getTime() - firstDate.getTime();
    });
};

/**
 * Reads the visible comment author from the saved action seller.
 */
const read_comment_author = (comment) => {
    const userId = comment?.seller?.userId;

    return {
        userId,
        firstname: comment?.seller?.name,
        avatarUrl: comment?.seller?.avatarUrl || (userId ? `${config.minio}/erisedev/${userId}/avatar.png` : "")
    };
};

/**
 * Reads the comment creation timestamp label.
 */
const read_comment_time = (comment) => {
    const date = new Date(comment?.createdAt || comment?.created || comment?.updatedAt);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
    });
};

/**
 * Converts ISO timestamps embedded in comment text into readable labels.
 */
const read_readable_date_text = (value) => {
    return `${value || ""}`.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z/g, (dateValue) => {
        return read_comment_time({ createdAt: dateValue }) || dateValue;
    });
};

/**
 * Ensures saved plain comments render inside typography tags.
 */
const read_typography_html = (value) => {
    const readableValue = read_readable_date_text(value);
    const hasHtmlTag = /<\/?[a-z][\s\S]*>/i.test(readableValue);

    if (hasHtmlTag) {
        return readableValue
            .replace(/<div\b([^>]*)>/gi, "<p$1>")
            .replace(/<\/div>/gi, "</p>");
    }

    const lines = readableValue.split("\n").map((line) => line.trim()).filter(Boolean);

    if (!lines.length) {
        return "<p></p>";
    }

    return lines.map((line) => `<p>${read_safe_html_text(line)}</p>`).join("");
};

/**
 * Reads the status saved with the comment, then the current lead status.
 */
const read_comment_status = (comment, currentStatus) => {
    return comment?.prospectStatus || comment?.leadStatus || currentStatus;
};

/**
 * Reads the quiet metadata line shown under the author name.
 */
const read_comment_meta = (comment, currentStatus) => {
    return [
        read_comment_time(comment),
        read_comment_status(comment, currentStatus)
    ].filter(Boolean).join(" • ");
};

/**
 * Renders Quill rich text comments for one prospect lead.
 */
export const Sales_Lead_Comments = (props) => {
    const { t } = useTranslation();
    const [commentHtml, setCommentHtml] = useState("");
    const [commentText, setCommentText] = useState("");
    const comments = read_comment_actions(props?.lead);

    /**
     * Adds the current Quill contents as one saved comment action.
     */
    const add_comment = () => {
        const cleanText = commentText.trim();

        if (!cleanText) {
            return;
        }

        props?.onAddComment(commentHtml);
        setCommentHtml("");
        setCommentText("");
    };

    return (
        <FormBox>
            <FormBoxTitle>
                <h4>{t("Comment")}</h4>
            </FormBoxTitle>
                <ProspectLeadComments>
                    {props?.locked ? null : (
                        <Sales_Lead_Quill_Field
                            value={commentHtml}
                            placeholder={t("Add a comment")}
                            onChange={(html, text) => {
                                setCommentHtml(html);
                                setCommentText(text);
                            }}
                        />
                    )}
                    {props?.locked ? null : (
                        <Main_Plain
                            text={t("Add comment")}
                            iconLeft="Add"
                            onClick={add_comment}
                        />
                    )}
                    <ProspectLeadCommentList>
                        {comments.map((comment, commentIndex) => (
                            <ProspectLeadComment key={comment?._id || comment?.id || commentIndex}>
                                <Sales_Lead_Avatar
                                    member={read_comment_author(comment)}
                                    label={comment?.seller?.name}
                                    name={comment?.seller?.name}
                                    size="3rem"
                                />
                                <ProspectLeadCommentBody>
                                    <ProspectLeadCommentMeta>
                                        <h5>{comment?.seller?.name || t("Unknown")}</h5>
                                        <h6>{read_comment_meta(comment, props?.status)}</h6>
                                    </ProspectLeadCommentMeta>
                                    <div dangerouslySetInnerHTML={{ __html: read_typography_html(comment?.comment) }} />
                                </ProspectLeadCommentBody>
                            </ProspectLeadComment>
                        ))}
                    </ProspectLeadCommentList>
                </ProspectLeadComments>
        </FormBox>
    );
};
